// src/pages/UrlsPage.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  TextField,
  Typography,
  Stack,
  Snackbar,
  Alert,
  Divider,
  Popover,
  Menu,
  MenuItem,
  Checkbox,
} from '@mui/material';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import { request } from '../utils/request';

const DOMAIN_REGEX = /^((https?:\/\/)?([\w-]+\.)+[\w-]{2,})(\/.*)?$/i;

export default function UrlsPage() {
  const [categories, setCategories] = useState([]);

  // dialog state
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    id: null,
    category_Name: '',
    Urls: [], // array of domains
    newUrlInput: '',
  });

  const mode = 'light'; // or 'dark'

  // delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingCat, setDeletingCat] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // snackbar
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');

  useEffect(() => {
    fetchCategories();
  }, []);

  // three-dot menu state for per-row actions
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);

  const handleMenuOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const onMenuEdit = (cat) => {
    handleMenuClose();
    openEdit(cat);
  };

  const onMenuCopy = (cat) => {
    handleMenuClose();
    copyUrls(cat.Urls);
  };

  const onMenuDelete = (cat) => {
    handleMenuClose();
    confirmDelete(cat);
  };

  const showSnack = (msg, severity = 'success') => {
    setSnackMsg(msg);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const handleSnackClose = (e, reason) => {
    if (reason === 'clickaway') return;
    setSnackOpen(false);
  };

  // fetch categories list
  const fetchCategories = async () => {
    try {
      const data = await request('urls', { method: 'get' });
      const arr = Array.isArray(data) ? data : [];
      const normalized = arr.map((d) => ({
        id: d._id,
        category_Name: d.category_Name,
        Urls: Array.isArray(d.Urls) ? d.Urls : [],
        createdAt: d.createdAt,
        updatedAt: d.updatedAt,
      }));
      setCategories(normalized);
    } catch (err) {
      console.error('fetchCategories error', err);
      showSnack('Failed to load categories.', 'error');
      setCategories([]);
    }
  };

  // open add dialog
  const openAdd = () => {
    setForm({
      id: null,
      category_Name: '',
      Urls: [],
      newUrlInput: '',
    });
    setIsEditing(false);
    setOpen(true);
  };

  // open edit dialog
  const openEdit = (cat) => {
    setForm({
      id: cat.id,
      category_Name: cat.category_Name,
      Urls: [...cat.Urls],
      newUrlInput: '',
    });
    setIsEditing(true);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  // handle text changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  // add single url from input
  const addSingleUrl = () => {
    const v = String(form.newUrlInput || '').trim();
    if (!v) return;
    // normalize: remove protocol for storage, strip trailing slashes
    const normalized = v.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
    if (!DOMAIN_REGEX.test(normalized) && !/^[\w.-]+\.[a-z]{2,}$/i.test(normalized)) {
      showSnack('Invalid domain/URL format.', 'error');
      return;
    }
    if (form.Urls.includes(normalized)) {
      showSnack('URL already added to this category.', 'warning');
      return;
    }
    setForm((p) => ({ ...p, Urls: [...p.Urls, normalized], newUrlInput: '' }));
  };

  const removeUrlAt = (index) => {
    setForm((p) => {
      const copy = [...p.Urls];
      copy.splice(index, 1);
      return { ...p, Urls: copy };
    });
  };

  // submit create or update
  const handleSubmit = async () => {
    const name = String(form.category_Name || '').trim();
    if (!name) {
      showSnack('Category name is required.', 'error');
      return;
    }

    // dedupe urls
    const normalizedUrls = Array.from(new Set((form.Urls || []).map((u) => String(u).trim()).filter(Boolean)));

    if (normalizedUrls.length === 0) {
      const proceed = window.confirm('You are creating a category with 0 URLs. Proceed?');
      if (!proceed) return;
    }

    try {
      if (isEditing && form.id) {
        await request(`urls/${form.id}`, {
          method: 'put',
          body: { category_Name: name, Urls: normalizedUrls },
        });
        showSnack('Category updated.', 'success');
      } else {
        await request('urls', { method: 'post', body: { category_Name: name, Urls: normalizedUrls } });
        showSnack('Category created.', 'success');
      }
      handleClose();
      fetchCategories();
    } catch (err) {
      console.error('save error', err);
      const msg = err?.response?.data?.message || err?.message || 'Failed to save category.';
      showSnack(msg, 'error');
    }
  };

  // delete category (single)
  const confirmDelete = (cat) => {
    setDeletingCat(cat);
    setDeleteOpen(true);
  };

  const cancelDelete = () => {
    setDeletingCat(null);
    setDeleteOpen(false);
    setDeleting(false);
  };

  const handleConfirmDelete = async () => {
    if (!deletingCat) return;
    setDeleting(true);
    try {
      await request(`urls`, { method: 'delete', params: deletingCat.id });
      showSnack('Category deleted.', 'success');
      cancelDelete();
      fetchCategories();
    } catch (err) {
      console.error('delete error', err);
      showSnack('Failed to delete category.', 'error');
      setDeleting(false);
    }
  };

  // Popover for "+N more"
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const [moreRowId, setMoreRowId] = useState(null);
  const [moreUrls, setMoreUrls] = useState([]);

  const handleMoreOpen = (event, rowId, urls) => {
    setMoreAnchorEl(event.currentTarget);
    setMoreRowId(rowId);
    setMoreUrls(urls || []);
  };

  const handleMoreClose = () => {
    setMoreAnchorEl(null);
    setMoreRowId(null);
    setMoreUrls([]);
  };

  const renderUrlChips = (arr = [], catId = '', options = {}) => {
    if (!arr || arr.length === 0) {
      return <Typography color="text.secondary">—</Typography>;
    }

    const preview = arr.slice(0, 6);
    const remainingCount = arr.length - preview.length;

    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        {/* Preview URLs */}
        {preview.map((u, i) => (
          <Chip
            key={i}
            size="small"
            label={u}
            onDelete={
              options.allowDelete
                ? () => options.onDelete && options.onDelete(i)
                : undefined
            }
            sx={{
              maxWidth: 220,
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          />
        ))}

        {/* +N more with dropdown */}
        {remainingCount > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: '#2d9cdb',
              userSelect: 'none',
            }}
            onClick={(e) => handleMoreOpen(e, catId || 'row', arr)}
          >
            <Typography variant="caption">
              +{remainingCount} more
            </Typography>
            <KeyboardArrowDownIcon fontSize="small" />
          </Box>
        )}
      </Stack>
    );
  };

  // copy url list to clipboard
  const copyUrls = async (arr = []) => {
    try {
      await navigator.clipboard.writeText((arr || []).join('\n'));
      showSnack('URLs copied to clipboard.', 'success');
    } catch {
      showSnack('Unable to copy to clipboard.', 'error');
    }
  };

  // ---------------------------
  // New: selection & bulk-delete state + handlers
  // ---------------------------
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const isAllSelected = categories.length > 0 && selectedIds.length === categories.length;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(categories.map((c) => c.id));
    }
  };

  const handleEditSelected = () => {
    if (selectedIds.length !== 1) return;
    const cat = categories.find((c) => c.id === selectedIds[0]);
    if (cat) {
      openEdit(cat);
      setSelectedIds([]); // clear selection (optional)
    }
  };

  const handleDeleteSelected = () => {
    if (selectedIds.length === 0) return;
    setBulkDeleteOpen(true);
  };

  const handleCancelBulkDelete = () => {
    setBulkDeleteOpen(false);
    setBulkDeleting(false);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      await Promise.all(
        selectedIds.map((id) => request('urls', { method: 'delete', params: id }))
      );
      showSnack(`${selectedIds.length} category(ies) deleted.`, 'success');
      setSelectedIds([]);
      await fetchCategories();
      handleCancelBulkDelete();
    } catch (err) {
      console.error('Bulk delete error', err);
      showSnack('Failed to delete selected categories.', 'error');
      setBulkDeleting(false);
    }
  };
  // ---------------------------

  return (
    <Box p={2} >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.95rem' }}>
          <Link
            href="/home"
            underline="hover"
            sx={{
              fontSize: '0.95rem',
              textDecoration: 'none',
              color: mode === 'dark' ? '#fff' : '#2d9cdb',
              '&:hover': {
                color: mode === 'dark' ? '#fff' :  '#0b1d51',
              },
            }}
          >
            Dashboard
          </Link>

          <Typography sx={{ fontSize: '0.95rem', color:'black' }}>
            Protocol Rules
          </Typography>
        </Breadcrumbs>


        <Button
          size="small"
          variant="contained"
          onClick={openAdd}
          
          sx={{
            backgroundColor: '#2d9cdb',
            '&:hover': { backgroundColor: '#0b1d51', color: '#fff' },
            px: 2,
            py: 0.8,
          }}
        >
          Add category
        </Button>
      </Stack>

      {/* --- Toolbar shown when rows selected (just above the table) --- */}
      {selectedIds.length > 0 && (
        <Box mb={1} display="flex" justifyContent="flex-start" alignItems="center" gap={1}>
          <Typography variant="body2">{selectedIds.length} selected</Typography>

          <Button
            size="small"
            variant="outlined"
            onClick={handleEditSelected}
            disabled={selectedIds.length !== 1}
            sx={{ textTransform: 'none' }}
          >
            Edit
          </Button>

          <Button
            size="small"
            variant="contained"
            onClick={handleDeleteSelected}
            sx={{ backgroundColor: '#e53935', '&:hover': { backgroundColor: '#b71c1c' }, color: '#fff', textTransform: 'none' }}
          >
            Delete
          </Button>

          <Button
            size="small"
            onClick={() => setSelectedIds([])}
            sx={{ textTransform: 'none' }}
          >
            Clear
          </Button>
        </Box>
      )}

      <Paper elevation={1} sx={{ mb: 2, height: 'calc(100vh - 170px)' }}>
        <Box sx={{ height: '100%', overflow: 'auto', width: '100%' }}>
          <Box
            component="table"
            width="100%"
            sx={{ borderCollapse: 'collapse', minWidth: '100%' }}
          >
            {/* Table head with sticky headers */}
            <Box component="thead">
              <Box component="tr">
                {/* New: checkbox header */}
                <Box
                  component="th"
                  p={1}
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: "#f5f7fb",
                    color: '#333',
                    width: 48,
                    textAlign: 'center',
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isAllSelected}
                    indeterminate={selectedIds.length > 0 && !isAllSelected}
                    onChange={toggleSelectAll}
                    inputProps={{ 'aria-label': 'select all categories' }}
                  />
                </Box>

                {['#', 'Category Name', 'URLs (preview)', 'Count', 'Actions'].map((h, i) => (
                  <Box
                    component="th"
                    key={i}
                    p={1}
                    textAlign={i === 4 ? 'center' : 'left'}
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      backgroundColor: "#f5f7fb",
                      color: '#333'
                    }}
                  >
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Table body */}
            <Box component="tbody">
              {categories.map((c, idx) => (
                <Box component="tr" key={c.id || idx}>
                  {/* row checkbox */}
                  <Box component="td" p={1} width={48} textAlign="center">
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(c.id)}
                      onChange={() => toggleSelect(c.id)}
                      inputProps={{ 'aria-label': `select category ${c.category_Name}` }}
                    />
                  </Box>

                  <Box component="td" p={1} width="40px">{idx + 1}</Box>

                  <Box component="td" p={1}>
                    <Typography fontWeight={600}>{c.category_Name}</Typography>
                  </Box>

                  <Box component="td" p={1}>
                    {renderUrlChips(c.Urls, c.id)}
                  </Box>

                  <Box component="td" p={1} width="100px">
                    {Array.isArray(c.Urls) ? c.Urls.length : 0}
                  </Box>

                  <Box component="td" p={1} textAlign="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <IconButton
                        size="small"
                        aria-controls={menuRowId === c.id ? `row-menu-${c.id}` : undefined}
                        aria-haspopup="true"
                        onClick={(e) => handleMenuOpen(e, c.id)}
                        sx={{ p: 0.5 }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>

                      <Menu
                        id={`row-menu-${c.id}`}
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && menuRowId === c.id}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      >
                        <MenuItem onClick={() => onMenuEdit(c)}>Edit</MenuItem>
                        <MenuItem onClick={() => onMenuCopy(c)}>Copy</MenuItem>
                        <MenuItem onClick={() => onMenuDelete(c)} sx={{ color: '#d32f2f' }}>Delete</MenuItem>
                      </Menu>
                    </Box>
                  </Box>
                </Box>
              ))}

              {categories.length === 0 && (
                <Box component="tr">
                  <Box component="td" colSpan={6} p={3} textAlign="center">
                    No categories yet. Click "Add Category" to create one.
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Popover showing URLs sized to the URL-preview area (covers only preview part) */}
      <Popover
        open={Boolean(moreAnchorEl)}
        anchorEl={moreAnchorEl}
        onClose={handleMoreClose}
        // align the popover to the left edge of the preview cell so it sits over that area
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            p: 1,
            // set the popover width to the URL-preview cell width (fall back to anchor width),
            // but don't exceed 420px so it stays compact
            width: moreAnchorEl
              ? Math.min(
                  (moreAnchorEl.closest && moreAnchorEl.closest('td')?.clientWidth) ||
                    moreAnchorEl.clientWidth,
                  420
                )
              : 420,
            maxHeight: 260,
            overflowY: 'auto',
            overflowX: 'hidden',
            boxShadow: 3,
            borderRadius: 1,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'flex-start',
            flexWrap: 'wrap', // wrap chips into multiple lines
            width: '100%',
            whiteSpace: 'normal',
            pr: 1,
          }}
        >
          {moreUrls.map((u, i) => (
            <Chip
              key={i}
              label={u}
              size="small"
              sx={{
                m: 0,
                maxWidth: '100%',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
              }}
            />
          ))}
        </Box>
      </Popover>

      {/* Add / Edit Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ backgroundColor: '#2d9cdb', color: '#fff', fontSize: '1rem', py: 1, textAlign: 'center' }}>
          {isEditing ? '' : 'Create Category'}
        </DialogTitle>

        <DialogContent sx={{ p: 2, }}>
          <Stack spacing={2}>
            <Box sx={{ pt: 1 }}>
              <TextField
                size="small"
                label="Category Name"
                name="category_Name"
                value={form.category_Name}
                onChange={handleFormChange}
                fullWidth
                placeholder="e.g. Social Media, E-commerce, Adult"
                sx={{ mt: 6 }}   // 👈 margin top
              />
            </Box>

            <Divider />

            <Typography variant="subtitle2">Add single URL / domain</Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                name="newUrlInput"
                value={form.newUrlInput}
                onChange={handleFormChange}
                placeholder="example.com or https://example.com/path"
                fullWidth
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addSingleUrl();
                  }
                }}
              />
              <Button variant="contained" size="small" sx={{
                bgcolor: '#2d9cdb',
                color: 'black',
                '&.MuiButton-contained:hover': {
                  bgcolor: '#0b1d51',
                  color: 'white',
                },
                transition: 'all 0.3s ease',
              }} onClick={addSingleUrl} startIcon={<AddIcon />}>
                Add
              </Button>
            </Stack>

            <Typography variant="subtitle2" sx={{ pt: 1 }}>
              Current URLs ({form.Urls.length})
            </Typography>
            <Paper variant="outlined" sx={{ p: 1, minHeight: 80 }}>
              {form.Urls.length ? (
                // show preview + "+N more" (clickable) — same helper works here
                renderUrlChips(form.Urls, form.id || 'form', { allowDelete: true, onDelete: removeUrlAt })
              ) : (
                <Typography color="text.secondary">No URLs added yet.</Typography>
              )}
            </Paper>

            <Typography variant="caption" color="text.secondary">
              Tip: store domain only (example.com) for broader matching, or include paths if you need fine-grained entries.
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 1, pr: 2 }}>
          <Button size="small" onClick={handleClose} sx={{ color: '#2d9cdb' }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSubmit}
            sx={{ backgroundColor: '#2d9cdb', '&:hover': { backgroundColor: '#0b1d51', color: '#fff' } }}
          >
            {isEditing ? 'edit' : 'Create Category'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteOpen} onClose={cancelDelete}>
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the category{' '}
            <strong>{deletingCat ? deletingCat.category_Name : ''}</strong>?
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            This will remove the category and its stored URLs.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 1, pr: 2 }}>
          <Button size="small" onClick={cancelDelete} disabled={deleting}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleting}
            sx={{ backgroundColor: '#e53935', '&:hover': { backgroundColor: '#b71c1c' } }}
          >
            {deleting ? 'Deleting…' : 'Yes, Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={bulkDeleteOpen}
        onClose={handleCancelBulkDelete}
        aria-labelledby="bulk-delete-dialog-title"
      >
        <DialogTitle id="bulk-delete-dialog-title">Delete Selected Categories</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the following {selectedIds.length} category(ies)?
          </Typography>
          <Box sx={{ mt: 1 }}>
            {selectedIds.map((id) => {
              const cat = categories.find((c) => c.id === id);
              return (
                <Typography key={id} variant="body2">
                  • {cat ? cat.category_Name : id}
                </Typography>
              );
            })}
          </Box>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 1, pr: 2 }}>
          <Button size="small" onClick={handleCancelBulkDelete} disabled={bulkDeleting}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleConfirmBulkDelete}
            disabled={bulkDeleting}
            sx={{
              backgroundColor: '#e53935',
              '&:hover': { backgroundColor: '#b71c1c' },
            }}
          >
            {bulkDeleting ? 'Deleting…' : `Yes, Delete (${selectedIds.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackOpen} autoHideDuration={3000} onClose={handleSnackClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleSnackClose} severity={snackSeverity} sx={{ width: '100%' }}>
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
